import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Card, Typography, Space, Alert, Row, Col, List, Badge, notification, Divider, Tag } from 'antd';
import { CameraOutlined, StopOutlined, ShoppingCartOutlined, CheckOutlined, SoundOutlined, BarcodeOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/library';
import { formatPriceWithCurrency, getFacilityCurrency } from '../../utils/currency';

const { Text } = Typography;

/*
Fresh, robust continuous scanner implementation:
- Reliable beep (WebAudio with resume on user gesture, fallback tone)
- Proper dedupe/cooldown so the same frame doesn't add repeatedly
- Confirmation modal if the immediately previous scanned product is scanned again
- Cart accumulation: increment quantity if same product, push new if different
- On-screen logs under the cart to help diagnose behavior
*/

const ContinuousBarcodeScanner = ({
  visible,
  onClose,
  onCheckout,
  availableItems = [],
  cart = [],
  onAddToCart,
  onUpdateCart,
  onRemoveFromCart,
  facilityId = null
}) => {
  // UI / scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [facilityCurrency, setFacilityCurrency] = useState('USD');

  // Duplicate confirmation flow
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);

  // Logging
  const [logs, setLogs] = useState([]); // [{ts,type,message,meta}]
  const [flashOk, setFlashOk] = useState(false); // flash corner guides on detection

  // Scan dedupe and audio
  const lastProcessedCodeRef = useRef(null); // last decoded (frame dedupe)
  const lastProcessedAtRef = useRef(0);
  const lastCommittedBarcodeRef = useRef(null); // last actually added/updated item
  const perCodeCooldownRef = useRef(new Map()); // barcode -> lastTs
  const nextAllowedScanAtRef = useRef(0); // global cooldown
  const audioCtxRef = useRef(null);

  // Video/scanner
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const manualInputRef = useRef(null);
  const manualSequenceStartRef = useRef(0);
  const manualLastChangeRef = useRef(0);
  const manualLastLengthRef = useRef(0);

  const addLog = (type, message, meta) => {
    setLogs((prev) => [{ ts: Date.now(), type, message, meta }, ...prev].slice(0, 200));
  };

  // Load facility currency
  useEffect(() => {
    if (!facilityId) return;
    
    const loadCurrency = async () => {
      try {
        const currency = await getFacilityCurrency(facilityId);
        setFacilityCurrency(currency);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    
    loadCurrency();
  }, [facilityId]);

  // Initialize scanner instance
  useEffect(() => {
    codeReaderRef.current = new BrowserMultiFormatReader();
    return () => {
      try { codeReaderRef.current?.reset(); } catch {}
    };
  }, []);

  // Stop scanner when modal is closed to free camera and reset lightweight state
  useEffect(() => {
    if (!visible) {
      try { codeReaderRef.current?.reset(); } catch {}
      setIsScanning(false);
      setIsLoading(false);
      setStatus('');
      setError('');
      setConfirmOpen(false);
      setPendingItem(null);
      setManualInput('');
      lastProcessedCodeRef.current = null;
      lastProcessedAtRef.current = 0;
      lastCommittedBarcodeRef.current = null;
      perCodeCooldownRef.current = new Map();
      nextAllowedScanAtRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Focus manual input when the scanner opens so HID scanners can send barcodes directly
  useEffect(() => {
    if (visible && manualInputRef.current) {
      const id = setTimeout(() => {
        manualInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(id);
    }
  }, [visible]);

  // Initialize audio context lazily
  const ensureAudio = async () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current?.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
    } catch (e) {
      // ignore
    }
  };

  const beep = async (freq = 820, ms = 90) => {
    try {
      await ensureAudio();
      const ctx = audioCtxRef.current;
      if (!ctx) return; // no audio available
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0.2;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      osc.start(now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + ms / 1000);
      osc.stop(now + ms / 1000);
    } catch (e) {
      // Best-effort, don't block
    }
  };

  const startScanning = async () => {
    setError('');
    setStatus('Initializing camera...');
    setIsLoading(true);
    addLog('info', 'Starting scanner');
    try {
      await ensureAudio(); // unlock audio on click path
      const reader = codeReaderRef.current;
      const devices = await reader.listVideoInputDevices();
      if (!devices || !devices.length) throw new Error('No camera devices found');

      let deviceId = devices[0].deviceId;
      const back = devices.find(d => d.label?.toLowerCase().includes('back'));
      if (back) deviceId = back.deviceId;

      setIsScanning(true);
      setStatus('Starting camera...');

      await reader.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result, err) => {
          if (!visible) return; // safety
          if (confirmOpen) return; // pause processing while confirming
          if (result) {
            const code = result.getText();
            const now = Date.now();
            if (now < nextAllowedScanAtRef.current) return; // global cooldown
            // short pre-confirmation detection beep for feedback
            beep(900, 60);
            setFlashOk(true);
            setTimeout(() => setFlashOk(false), 180);
            onDecoded(code, now);
          }
          // err can be a normal decode attempt miss; ignore
        }
      );

      setStatus('Camera ready. Point at a barcode.');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to start camera');
      setStatus('');
      setIsScanning(false);
    } finally {
      setIsLoading(false);
    }
  };

  const stopScanning = () => {
    try { codeReaderRef.current?.reset(); } catch {}
    setIsScanning(false);
    setStatus('Scanner stopped');
    addLog('info', 'Scanner stopped');
  };

  // Core decode handler
  const onDecoded = (barcode, nowArg) => {
    const now = nowArg || Date.now();

    // Frame dedupe: avoid spamming the same code if camera lingers
    const lastCode = lastProcessedCodeRef.current;
    const lastTs = lastProcessedAtRef.current;
    if (barcode === lastCode && now - lastTs < 300) {
      return; // too soon from same frame
    }

    // Per-code rate limit: avoid many same-code scans in sub-second
    const lastForCode = perCodeCooldownRef.current.get(barcode) || 0;
    if (now - lastForCode < 700) {
      return;
    }
    perCodeCooldownRef.current.set(barcode, now);

    lastProcessedCodeRef.current = barcode;
    lastProcessedAtRef.current = now;

    addLog('scan', 'Barcode detected', { barcode });

    const item = availableItems.find(i => i.barcode === barcode);
    if (!item) {
      addLog('warn', 'Item not found for barcode', { barcode });
      notification.warning({ message: 'Item Not Found', description: `No item with barcode ${barcode}` });
      nextAllowedScanAtRef.current = now + 200;
      return;
    }

    // If immediate previous committed item is the same, require confirmation
    if (lastCommittedBarcodeRef.current && lastCommittedBarcodeRef.current === item.barcode) {
      setPendingItem(item);
      setConfirmOpen(true);
      addLog('info', 'Duplicate scan detected - awaiting confirmation', { name: item.name, barcode });
      nextAllowedScanAtRef.current = now + 500; // block until user decides
      return;
    }

    // Otherwise commit directly
    commitAdd(item);
    nextAllowedScanAtRef.current = now + 250; // brief global cooldown
  };

  const commitAdd = (item) => {
    // If exists, increment; else add
    const existing = cart.find(c => c.barcode === item.barcode || c.name === item.name);
    if (existing) {
      const nextQty = (existing.quantity || 0) + 1;
      onUpdateCart?.(existing.name, nextQty);
      addLog('add', 'Incremented item quantity', { name: existing.name, qty: nextQty });
      notification.success({ message: 'Item Updated', description: `${existing.name} → ${nextQty}` });
    } else {
      onAddToCart?.(item, 1);
      addLog('add', 'Added new item to cart', { name: item.name });
      notification.success({ message: 'Item Added', description: `${item.name} added` });
    }
    beep();
    setStatus(`Last scanned: ${item.name}`);
    lastCommittedBarcodeRef.current = item.barcode || null;
  };

  const handleConfirmAdd = () => {
    if (pendingItem) {
      commitAdd(pendingItem);
    }
    setPendingItem(null);
    setConfirmOpen(false);
  };

  const handleCancelAdd = () => {
    addLog('info', 'Duplicate scan cancelled');
    setPendingItem(null);
    setConfirmOpen(false);
  };

  const handleCheckout = () => {
    if (!cart?.length) {
      notification.warning({ message: 'Empty Cart', description: 'Scan items before checkout' });
      return;
    }
    onCheckout?.();
    onClose?.();
  };

  const getStatusAlert = () => {
    if (error) return <Alert message={error} type="error" showIcon />;
    if (status) return <Alert message={status} type="info" showIcon />;
    return null;
  };

  const handleManualSubmit = () => {
    const value = manualInput.trim();
    if (!value) return;

    const item = availableItems.find(i => i.barcode === value);
    if (!item) {
      addLog('warn', 'Item not found for manual/scanner barcode', { barcode: value });
      notification.warning({ message: 'Item Not Found', description: `No item with barcode ${value}` });
      setManualInput('');
      return;
    }

    commitAdd(item);
    setManualInput('');
    manualLastLengthRef.current = 0;
    manualLastChangeRef.current = 0;
    manualSequenceStartRef.current = 0;
  };

  const handleManualChange = (e) => {
    const value = e.target.value;
    const now = Date.now();
    const prevLen = manualLastLengthRef.current || 0;
    const lastChange = manualLastChangeRef.current || 0;

    if (!lastChange || now - lastChange > 300) {
      manualSequenceStartRef.current = now;
    }

    manualLastChangeRef.current = now;
    manualLastLengthRef.current = value.length;
    setManualInput(value);

    const seqStart = manualSequenceStartRef.current || now;
    const isScannerLike = value.length >= 6 && (now - seqStart) <= 180 && value.length - prevLen >= 4;

    if (isScannerLike) {
      const trimmed = value.trim();
      if (trimmed) {
        const item = availableItems.find(i => i.barcode === trimmed);
        if (!item) {
          addLog('warn', 'Item not found for manual/scanner barcode', { barcode: trimmed });
          notification.warning({ message: 'Item Not Found', description: `No item with barcode ${trimmed}` });
        } else {
          commitAdd(item);
        }
        setManualInput('');
        manualLastLengthRef.current = 0;
        manualLastChangeRef.current = 0;
        manualSequenceStartRef.current = 0;
      }
    }
  };

  // Calculate totals by currency
  const totalsByCurrency = cart.reduce((acc, item) => {
    const itemCurrency = item.currency || facilityCurrency;
    if (!acc[itemCurrency]) {
      acc[itemCurrency] = 0;
    }
    acc[itemCurrency] += (item.price || 0) * (item.quantity || 0);
    return acc;
  }, {});
  
  // For backward compatibility, get total in facility currency
  const cartTotal = totalsByCurrency[facilityCurrency] || 0;

  if (!visible) return null;

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CameraOutlined style={{ color: 'var(--primary-color)' }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Continuous Barcode Scanner</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Scan items continuously; quantities will accumulate.</div>
            </div>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width="95%"
        style={{ maxWidth: 1200 }}
        footer={null}
        destroyOnClose
        centered
      >
        <div>
          {getStatusAlert()}

          <Row gutter={[16, 16]}>
            {/* Scanner panel */}
            <Col xs={24} lg={14}>
              <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ position: 'relative', background: '#000', height: 420 }}>
                  <video
                    ref={videoRef}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: isScanning ? 'block' : 'none' }}
                    playsInline
                    muted
                  />
                  {!isScanning && (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#f5f7fa,#c3cfe2)' }}>
                      <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <CameraOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>Camera Ready</div>
                        <div style={{ fontSize: 12 }}>Click Start Scanning to begin</div>
                      </div>
                    </div>
                  )}
                  {isScanning && (
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                      <CornerGuides flash={flashOk} />
                      <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(0,0,0,0.35)', color: 'var(--text-inverse)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                        Aim the barcode within the guide corners
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12}>
                  {!isScanning ? (
                    <Button type="primary" size="large" icon={<CameraOutlined />} onClick={startScanning} loading={isLoading} disabled={isLoading} block>
                      {isLoading ? 'Starting Camera...' : 'Start Scanning'}
                    </Button>
                  ) : (
                    <Button size="large" icon={<StopOutlined />} onClick={stopScanning} block>
                      Stop Scanning
                    </Button>
                  )}
                </Col>
                <Col xs={24} sm={12}>
                  <Button type="primary" size="large" icon={<CheckOutlined />} onClick={handleCheckout} block style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                    Go to Checkout ({cart.reduce((s,i)=>s+(i.quantity||0),0)})
                  </Button>
                </Col>
              </Row>

              {/* Manual / HID scanner input */}
              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                <Col xs={24}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      ref={manualInputRef}
                      value={manualInput}
                      onChange={handleManualChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleManualSubmit();
                        }
                      }}
                      placeholder="Type or scan barcode here"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--border-color, #d9d9d9)',
                        fontSize: 14
                      }}
                    />
                    <Button
                      type="default"
                      onClick={handleManualSubmit}
                      disabled={!manualInput.trim()}
                    >
                      Apply
                    </Button>
                  </div>
                  <div style={{ fontSize: 11, marginTop: 4, color: 'var(--text-secondary)' }}>
                    If you use a USB/Bluetooth barcode scanner, keep this field focused and just scan – items will be added automatically.
                  </div>
                </Col>
              </Row>
            </Col>

            {/* Cart + Logs */}
            <Col xs={24} lg={10}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShoppingCartOutlined />
                    <span>Current Cart</span>
                    <Badge count={cart.reduce((s,i)=>s+(i.quantity||0),0)} style={{ backgroundColor: '#52c41a' }} />
                  </div>
                }
                bodyStyle={{ paddingTop: 8 }}
              >
                {cart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-tertiary)' }}>
                    <ShoppingCartOutlined style={{ fontSize: 40, marginBottom: 8 }} />
                    <div>No items in cart</div>
                    <div style={{ fontSize: 12 }}>Start scanning to add items</div>
                  </div>
                ) : (
                  <>
                    <List
                      dataSource={expandCartDisplay(cart)}
                      style={{ maxHeight: 220, overflowY: 'auto' }}
                      renderItem={(row) => (
                        <List.Item
                          actions={[
                            <Button type="link" danger size="small" onClick={() => {
                              const current = cart.find(c => c.name === row.item.name);
                              if (!current) return;
                              const newQty = (current.quantity || 0) - 1;
                              if (newQty > 0) onUpdateCart?.(current.name, newQty);
                              else onRemoveFromCart?.(current.name);
                            }}>Remove one</Button>
                          ]}
                        >
                          <List.Item.Meta
                            title={<div style={{ fontWeight: 600 }}>{row.item.name}</div>}
                            description={
                              <div style={{ fontSize: 12 }}>
                                {row.item.barcode && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <BarcodeOutlined /> <Text code>{row.item.barcode}</Text>
                                  </div>
                                )}
                                <div>Each: {formatPriceWithCurrency(Number(row.item.price), row.item.currency || facilityCurrency, facilityId)}</div>
                              </div>
                            }
                          />
                          <div style={{ fontWeight: 700 }}>{formatPriceWithCurrency(Number(row.item.price), row.item.currency || facilityCurrency, facilityId)}</div>
                        </List.Item>
                      )}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ textAlign: 'center' }}>
                      {Object.keys(totalsByCurrency).length > 1 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {Object.entries(totalsByCurrency)
                            .sort((a, b) => b[1] - a[1])
                            .map(([currency, total]) => (
                              <div key={currency} style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary-color)' }}>
                                {currency}: {formatPriceWithCurrency(total, currency, facilityId)}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-color)' }}>
                          Total: {formatPriceWithCurrency(cartTotal, Object.keys(totalsByCurrency)[0] || facilityCurrency, facilityId)}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{cart.reduce((s,i)=>s+(i.quantity||0),0)} unit(s) in cart</div>
                    </div>
                  </>
                )}
              </Card>

              {/* Logs panel under cart */}
              <Card size="small" style={{ marginTop: 12 }} title={
                <Space>
                  <InfoCircleOutlined />
                  <span>Scan Logs</span>
                </Space>
              } extra={
                <Space size={8}>
                  <Tag>{logs.length}</Tag>
                  <Button size="small" icon={<CopyOutlined />} onClick={() => {
                    try {
                      const text = logs
                        .slice(0, 100)
                        .map(l => `${new Date(l.ts).toISOString()} [${l.type.toUpperCase()}] ${l.message}${l.meta ? ' ' + JSON.stringify(l.meta) : ''}`)
                        .join('\n');
                      navigator.clipboard.writeText(text);
                      notification.success({ message: 'Logs Copied', description: 'Recent logs copied to clipboard.' });
                    } catch (e) {
                      notification.error({ message: 'Copy Failed', description: e.message });
                    }
                  }}>Copy</Button>
                </Space>
              }>
                {logs.length === 0 ? (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Logs will appear here as you scan.</div>
                ) : (
                  <List
                    size="small"
                    dataSource={logs}
                    style={{ maxHeight: 180, overflowY: 'auto' }}
                    renderItem={(l) => (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Space size={6}>
                              {l.type === 'scan' && <Tag color="blue">SCAN</Tag>}
                              {l.type === 'add' && <Tag color="green">ADD</Tag>}
                              {l.type === 'warn' && <Tag color="orange">WARN</Tag>}
                              {l.type === 'info' && <Tag>INFO</Tag>}
                              <span>{l.message}</span>
                            </Space>
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{new Date(l.ts).toLocaleTimeString()}</span>
                          </div>
                          {l.meta && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
                              {Object.entries(l.meta).map(([k, v]) => (
                                <span key={k} style={{ marginRight: 8 }}>
                                  <Text type="secondary">{k}:</Text> {String(v)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Duplicate confirm modal */}
      <Modal
        title="Confirm Duplicate Scan"
        open={confirmOpen}
        onOk={handleConfirmAdd}
        onCancel={handleCancelAdd}
        okText="Add Another"
        cancelText="Cancel"
        okButtonProps={{ type: 'primary', style: { background: '#52c41a', borderColor: '#52c41a' } }}
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <SoundOutlined style={{ fontSize: 42, color: '#faad14' }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>Same product scanned again</div>
          <div style={{ marginTop: 6 }}>
            {pendingItem ? (
              <Space direction="vertical" size={2}>
                <div>{pendingItem.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Price: {formatPriceWithCurrency(Number(pendingItem.price), pendingItem.currency || facilityCurrency, facilityId)}</div>
              </Space>
            ) : (
              <div>Pending item</div>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>Add one more to the cart?</div>
        </div>
      </Modal>
    </>
  );
};

export default ContinuousBarcodeScanner;

// Corner guides overlay component (inline for simplicity)
function CornerGuides({ flash }) {
  const color = flash ? 'var(--success-color)' : 'rgba(255,255,255,0.9)';
  const thick = 4;
  const size = 28;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* mask */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
      {/* box area */}
      <div style={{ position: 'absolute', top: '18%', left: '12%', width: '76%', height: '56%' }}>
        {/* top-left corner */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* top-right */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* bottom-left */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* bottom-right */}
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
      </div>
    </div>
  );
}

// Expand cart items into repeated rows for each unit
function expandCartDisplay(cart) {
  const rows = [];
  cart.forEach(item => {
    const qty = Number(item.quantity) || 0;
    for (let i = 0; i < qty; i++) {
      rows.push({ key: `${item.name}-${i}`, item });
    }
  });
  return rows;
}
